import {
  defaultRightToWorkStatus,
  paymentModels,
  regulationOptions,
} from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { formatFullName } from '@nxsys/utils';
import { ColumnsType } from 'antd/es/table';
import { find } from 'lodash';
import moment from 'moment';

export const getRTWColumns = ({
  messages,
  isBureau,
}: {
  messages: TypedMessages;
  isBureau?: boolean;
}): ColumnsType<IRTW> => {
  return [
    {
      title: messages['common.systemId'],
      key: 'id',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 200,
      render: (value) => value,
    },
    {
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      width: 300,
      render: (record) =>
        `${record?.primaryAgency?.agencyPrefix || '___'}/${
          record?.agencyRef || record?.id || '_'
        }`,
    },
    {
      key: 'companyPayment',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'companyPayment',
      width: 300,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.companyName || '_',
    },
    {
      title: messages['common.name'],
      key: 'name',
      width: 250,
      render: (_value, record) => formatFullName(record) || '_',
    },
    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],
      dataIndex: 'setting',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        paymentModels[value?.paymentModel as keyof typeof paymentModels] || 'Not Set',
      width: 200,
    },
    {
      key: 'primaryAgency',
      title: messages['common.primaryAgency'],
      dataIndex: 'primaryAgency',
      width: 350,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.name || '_',
    },
    {
      key: 'secondaryAgency',
      title: 'Secondary Agency',
      dataIndex: 'secondaryAgency',
      width: 350,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.name || '_',
    },
    {
      title: messages['common.rightToWorkStatus'],
      key: 'rightToWorkStatus',
      dataIndex: 'rightToWorkStatus',
      align: 'center',
      width: 240,
      render: (value) =>
        value
          ? find(defaultRightToWorkStatus, (e) => e.value === value)?.label
          : '_',
    },
    {
      title: messages['common.documentExpireDate'],
      key: 'documentExpiryDate',
      dataIndex: 'documentExpiryDate',
      align: 'right',
      width: 240,
      render: (documentExpiryDate) =>
        documentExpiryDate
          ? moment(documentExpiryDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      title: messages['common.shareCodeECCheckCode'],
      key: 'shareCodeVerified',
      dataIndex: 'shareCodeVerified',
      width: 240,
      render: (value) => value,
    },
  ];
};

export const getAWRColumns = ({
  messages,
  isBureau,
}: {
  messages: TypedMessages;
  isBureau?: boolean;
}): ColumnsType<IAWR> => {
  return [
    {
      title: messages['common.systemId'],
      key: 'id',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 200,
      render: (value) => value,
    },
    {
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      width: 300,
      render: (record) =>
        `${record?.primaryAgency?.agencyPrefix || '___'}/${
          record?.agencyRef || record?.id || '_'
        }`,
    },
    {
      title: messages['common.name'],
      key: 'name',
      width: 250,
      render: (_value, record) => formatFullName(record) || '_',
    },
    {
      key: 'companyPayment',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'companyPayment',
      width: 300,
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) => value?.companyName || '_',
    },

    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],
      dataIndex: 'setting',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) =>
        paymentModels[value?.paymentModel as keyof typeof paymentModels] || 'Not Set',
      width: 200,
    },
    {
      key: 'awrWeek',
      title: messages['common.awrWeek'],
      dataIndex: 'awrWeek',
      align: 'right',
      // sorter: true,
      // Blank when AWR tracking is off for the worker; 0 is a real count.
      render: (awrWeek) => awrWeek ?? '',
      width: 200,
    },
    {
      key: 'taxWeek',
      title: messages['common.taxWeek'],
      dataIndex: 'latestPayroll',
      align: 'right',
      render: (latestPayroll) => latestPayroll?.taxPeriod || '',
      width: 200,
    },
    {
      key: 'primaryAgency',
      title: messages['common.primaryAgency'],
      dataIndex: 'primaryAgency',
      width: 350,
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) => value?.name || '_',
    },
    {
      key: 'Secondary Agency',
      title: messages['common.secondaryAgency'],
      dataIndex: 'secondaryAgency',
      width: 350,
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) => value?.name || '_',
    },
  ];
};

export const getWTRColumns = ({
  messages,
  isBureau,
}: {
  messages: TypedMessages;
  isBureau?: boolean;
}): ColumnsType<IWTR> => {
  return [
    {
      title: messages['common.systemId'],
      key: 'id',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 200,
      render: (value) => value,
    },
    {
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      width: 300,
      render: (record) =>
        isBureau
          ? (record?.agencyRef || record?.id || '_')
          : `${record?.primaryAgency?.agencyPrefix || '___'}/${
              record?.agencyRef || record?.id || '_'
            }`,
    },
    {
      title: messages['common.name'],
      key: 'name',
      width: 250,
      render: (_value, record) => formatFullName(record) || '_',
    },
    {
      key: 'companyPayment',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'companyPayment',
      width: 300,
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) => value?.companyName || '_',
    },

    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],
      dataIndex: 'setting',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (value) =>
        paymentModels[value?.paymentModel as keyof typeof paymentModels] || 'Not Set',
      width: 200,
    },

    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      dataIndex: 'taxPeriod',
      align: 'right',
      width: 200,
    },
    {
      key: 'totalHour',
      title: messages['common.noOfHoursWorked'],
      dataIndex: 'totalHour',
      align: 'right',
      // sorter: true,
      width: 200,
    },
    {
      key: 'workingTimeRegulation',
      title: messages['common.status'],
      dataIndex: 'workingTimeRegulation',
      align: 'center',
      width: 200,
      render: (val) =>
        find(regulationOptions, (o) => o?.value === val)?.label || '',
    },
    {
      key: 'isBreached',
      title: messages['common.breached'],
      align: 'center',
      render: (data) => {
        const val =
          data.workingTimeRegulation !== 'OPT_IN'
            ? 'N/A'
            : data?.isBreached
              ? 'Yes'
              : 'No';
        const color =
          data.workingTimeRegulation !== 'OPT_IN'
            ? '#666666'
            : data?.isBreached
              ? 'red'
              : 'green';
        return <span style={{ color }}>{val}</span>;
      },
      width: 200,
    },
  ];
};
